import {
    Box,
    Grid,
    TextField,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableBody,
    TableRow,
    ButtonGroup,
    Button,
    Modal
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { docDeviationsSelectors, docTestsSelectors } from "../../documentSlice";
import { formatDate, impossible, parseISODate } from "../../../../app/Utils";
import { useState } from "react";
import { DatePicker, DateValidationError, PickerChangeHandlerContext } from "@mui/x-date-pickers";
import { LoadingButton } from "@mui/lab";
import { analyseDeviation, closeDeviation, correctionDeviation, Deviation, DeviationClassification, DeviationStatus, evaluateDeviation, updateDeviation } from "./deviationsApi";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { PersonData, PersonalDataPicker } from "../../PersonalDataPicker";
import { LoadUsers } from "../../../../app/AppDataLoader";
import { LoadingContainer } from "../../../../app/LoadingContainer";
import { selectLoggedUserId } from "../../../user/userSlice";
import { selectUserById } from "../../../users/usersSlice";
import { RequiredError } from "../../../../app/components/RequiredError";
import { If } from "../../../../app/If";
import { YesNoChip } from "../../../../app/components/YesNoChip";
import { ClassificationChip, StatusChip } from "./Utils";
import { DocumentTestLoader } from "../../documentTests/DocumentTestLoader";
import { selectDocument } from '../../documentSlice';
import { Localized } from "@fluent/react";
import CloseIcon from '@mui/icons-material/Close';
import { TestPreview } from "../../documentTests/TestPreview";
import LaunchIcon from '@mui/icons-material/Launch';
import CheckIcon from '@mui/icons-material/Check';
import { DocumentTestOutcomeChip } from "../../documentTests/utils/DocumentTestOutcomeChip";
import TaskAltIcon from '@mui/icons-material/TaskAlt'

export interface DeviationFormProps {
    id: number
    close: () => void
}
export default function DeviationForm(props: DeviationFormProps) {
    return (
        <LoadUsers component={<LoadingContainer />}>
            <DocumentTestLoader asyncLoading>
                <Box sx={{ p: 3, bgcolor: "#fff" }}>
                    {/* OPIS ODCHYLENIA */}
                    <DeviationDescription id={props.id} />

                    {/* ANALIZA ODCHYLENIA */}
                    <DeviationAnalysis id={props.id} />
                    <Box sx={{ my: 3 }} />

                    {/* OCENA ODCHYLENIA */}
                    <DeviationEvaluation id={props.id} />
                    <Box sx={{ my: 3 }} />

                    {/* DZIAŁANIA NAPRAWCZE */}
                    <DeviationCorrection id={props.id} />
                    <Box sx={{ my: 3 }} />

                    {/* ZAMKNIĘCIE ODCHYLENIA */}
                    <DeviationClosure id={props.id} />
                </Box>
            </DocumentTestLoader>
        </LoadUsers>
    );
}

const TestName = (props: { id: number }) => {
    const loaded = useAppSelector(state => selectDocument(state).doc?.tests.loaded ?? false)
    const test = useAppSelector(state => docTestsSelectors.selectById(state, props.id))

    if (!loaded) {
        return <Typography fontSize='small' color='text-secondary'><Localized id='loading-data'>Ładowanie danych...</Localized></Typography>
    } else if (test) {
        return <>{test.testSpecification.name}</>
    } else {
        return <span>
            <Localized id='no-data'>Brak danych</Localized>
        </span>
    }
}

const TestsTable = (props: { deviation: Deviation }) => {
    const { tests } = props.deviation

    const loaded = useAppSelector(state => selectDocument(state).doc?.tests.loaded ?? false)

    const [preview, setPreview] = useState<number | undefined>(undefined)

    const test = useAppSelector(state => docTestsSelectors.selectById(state, preview ?? 0))

    return <TableContainer component={Paper}>
        <Table size='small'>
            <caption><small>Testy związane z odchyleniem</small></caption>
            <TableHead>
                <TableCell width={60}>#</TableCell>
                <TableCell>Nazwa testu</TableCell>
                <TableCell width={160}></TableCell>
            </TableHead>
            <TableBody>
                {tests.map((t, idx) => <TableRow>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell><TestName id={t.documentTestId} /></TableCell>
                    <TableCell>{
                        loaded
                            ? <Button startIcon={<LaunchIcon />} sx={{ my: 0, py: 0, }} size='small' onClick={() => setPreview(t.documentTestId)}><Localized id='open'>Otwórz</Localized></Button>
                            : <Typography fontSize='small' color='text.secondary'><Localized id='waiting'>Czekaj...</Localized></Typography>
                    }</TableCell>
                </TableRow>)}
            </TableBody>
        </Table>
        <Modal open={preview !== undefined}>
            <Box sx={{
                height: '90vh',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                bgcolor: 'background.paper',
                border: '1px solid #000',
                boxShadow: 24,
                p: 2,
            }}>
                <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    overflowY: 'auto',
                }}>
                    {preview && test && <TestPreview test={test} />}
                </Box>
                <ButtonGroup>
                    <Button sx={{
                        ml: 'auto',
                    }} onClick={() => setPreview(undefined)} startIcon={<CloseIcon />} variant='outlined'><Localized id='close'>Zamknij</Localized></Button>
                </ButtonGroup>
            </Box>
        </Modal>
    </TableContainer>
}

const DeviationDescription = (props: { id: number }) => {
    const dispatch = useAppDispatch()

    const entity = useAppSelector(state => docDeviationsSelectors.selectById(state, props.id)) ?? impossible()

    const ro = entity.status !== 'NEW'

    const [saving, setSaving] = useState(false)

    const [expectedResult, setExpectedResult] = useState< string>(entity?.expectedResult ?? '')
    const [actualResult, setactualResult] = useState< string>(entity?.actualResult ?? '')
    const [reporter, setReporter] = useState<PersonData>({
        fullName: entity.reporterFullName ?? '',
        userName: entity.reporterUserName ?? '',
        email: '',
        position: entity.reporterPosition ?? '',
        companyName: entity.reporterCompany ?? ''
    })
    const [reporterSignatureDate, setReporterSignatureDate ] = useState< string | null>(entity?.reporterSignatureDate ?? '')

    const reporterSignatureDateValue: Date | null = reporterSignatureDate ? parseISODate(reporterSignatureDate) : null
    const handleReporterSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setReporterSignatureDate(null)
        } else {
            setReporterSignatureDate(date.toISOString())
        }
    }

    const handleSaveClick = async () => {
        try {
            setSaving(true)
            await dispatch(updateDeviation({...entity,
                expectedResult,
                actualResult,
                reporterUserName: reporter.userName,
                reporterFullName: reporter.fullName,
                reporterPosition: reporter.position,
                reporterCompany: reporter.companyName,
                reporterSignatureDate,
            })).unwrap()
            dispatch(showSuccess('saved'))
        } catch (error) {
            console.log(error)
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    return <Paper sx={{ pb: 1, }}>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}
            sx={{
                display: 'flex',
            }}
        >
            <Typography variant="body1" fontWeight={700}>Opis odchylenia</Typography>
            {ro && <TaskAltIcon sx={(t) => { return {
                color: t.palette.success.light,
                ml: 'auto',
            }}} />}
        </Box>
        <Grid sx={{ px: 1, py: 2, }} container spacing={2}>
            <Grid item xs={12} md={4} sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
            }}>
                <Typography variant="h5">{`Odchylenie nr ${entity.order}`}</Typography>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                }}>
                    <Typography variant="caption">Status</Typography>
                    <StatusChip status={entity.status} />
                </Box>
            </Grid>
            <Grid item xs={12} md={8}>
                <TestsTable deviation={entity} />
            </Grid>
            <Grid item xs={12} md={12}>
                <TextField fullWidth
                    inputProps={{
                        'readonly': ro ? 'true' : undefined
                    }}
                    label="Oczekiwany rezultat" multiline minRows={3} value={expectedResult} onChange={e => setExpectedResult(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={12}>
                <TextField fullWidth
                    inputProps={{
                        'readonly': ro ? 'true' : undefined
                    }}
                    label="Aktualny rezultat" multiline minRows={3} value={actualResult} onChange={e => setactualResult(e.target.value)} />
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            <Grid item md={6}>
                <PersonalDataPicker styles={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    px: 1,
                    mt: 2,
                }}
                    label={'Osoba zgłaszająca odchylenie'}
                    data={reporter}
                    setData={setReporter}
                    readonly={ro}
                />
            </Grid>
            <Grid item md={6}>
                <DatePicker
                    sx={{ float: 'right', mr: 1 }}
                    label="Data"
                    readOnly={ro}
                    value={reporterSignatureDateValue} onChange={handleReporterSignatureDateChange} />
            </Grid>
        </Grid>
        {!ro && <ButtonGroup sx={{ mt: 3, px: 1 }}>
            <LoadingButton loading={saving} variant="outlined" onClick={handleSaveClick}>Zapisz</LoadingButton>
        </ButtonGroup>}
    </Paper>
}

const DeviationEvaluation = (props: { id: number }) => {
    const dispatch = useAppDispatch()

    const entity = useAppSelector(state => docDeviationsSelectors.selectById(state, props.id)) ?? impossible()
    const userId = useAppSelector(selectLoggedUserId)
    const user = useAppSelector(st => selectUserById(st, userId))

    const [saving, setSaving] = useState(false)
    const [showErr, setShowErr] = useState(false)

    const ro = !(entity.status === 'ANALYZED' || entity.status === 'EVALUATED')

    const hiddenStatuses: DeviationStatus[] = ['NEW']
    const hidden = hiddenStatuses.findIndex(s => s === entity.status) >= 0

    const [hasImpactOnProductQuality, setHasImpactOnProductQuality] = useState<boolean>(entity?.hasImpactOnProductQuality ?? false)
    const [classification, setClassification] = useState<DeviationClassification>(entity?.classification ?? 'MINOR')
    const [isQualificationProcessContinuationAllowed, setIsQualificationProcessContinuationAllowed] = useState<boolean>(entity?.isQualificationProcessContinuationAllowed ?? false)
    const [evaluationJustification, setEvaluationJustification] = useState<string>(entity?.evaluationJustification ?? '')

    const [decisionMaker, setDecisionMaker] = useState<PersonData>({
        fullName: entity.decisionMakerFullName ?? '',
        userName: entity.decisionMakerUserName ?? '',
        email: '',
        position: entity.decisionMakerPosition ?? '',
        companyName: entity.decisionMakerCompany ?? ''
    })
    const [decisionMakerSignatureDate, setDecisionMakerSignatureDate ] = useState< string | null>(entity?.decisionMakerSignatureDate ?? '')
    const decisionMakerSignatureDateValue: Date | null = decisionMakerSignatureDate ? parseISODate(decisionMakerSignatureDate) : null
    const handleDecisionMakerSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setDecisionMakerSignatureDate(null)
        } else {
            setDecisionMakerSignatureDate(date.toISOString())
        }
    }

    const handleSaveClick = async () => {
        setShowErr(true)
        if (evaluationJustification)
        {
            try {
                setSaving(true)
                const saved = await dispatch(evaluateDeviation({
                    hasImpactOnProductQuality,
                    classification,
                    isQualificationProcessContinuationAllowed,
                    evaluationJustification,
                    decisionMakerUserName: entity.decisionMakerUserName,
                    decisionMakerFullName: entity.decisionMakerFullName,
                    decisionMakerPosition: entity.decisionMakerPosition,
                    decisionMakerCompany: entity.decisionMakerCompany,
                    decisionMakerSignatureDate: entity.decisionMakerSignatureDate,
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setDecisionMaker({
                    userName: saved.decisionMakerUserName,
                    fullName: saved.decisionMakerFullName ?? '',
                    email: '',
                    position: saved.decisionMakerPosition ?? '',
                    companyName: saved.decisionMakerCompany ?? '',
                })
                setDecisionMakerSignatureDate(saved.decisionMakerSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    const handleEvaluateClick = async () => {
        setShowErr(true)
        if (evaluationJustification)
        {
            try {
                setSaving(true)
                const saved = await dispatch(evaluateDeviation({
                    hasImpactOnProductQuality,
                    classification,
                    isQualificationProcessContinuationAllowed,
                    evaluationJustification,
                    decisionMakerUserName: user?.userName ?? null,
                    decisionMakerFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                    decisionMakerPosition: user?.position ?? null,
                    decisionMakerCompany: user?.companyName ?? null,
                    decisionMakerSignatureDate: new Date().toISOString(),
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setDecisionMaker({
                    userName: saved.decisionMakerUserName,
                    fullName: saved.decisionMakerFullName ?? '',
                    email: '',
                    position: saved.decisionMakerPosition ?? '',
                    companyName: saved.decisionMakerCompany ?? '',
                })
                setDecisionMakerSignatureDate(saved.decisionMakerSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    if (hidden) return null
    else return <Paper sx={{ pb: 1, mt: 3 }}>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}
            sx={{
                display: 'flex',
            }}
        >
            <Typography variant="body1" fontWeight={700}>Ocena odchylenia</Typography>
            {ro && <TaskAltIcon sx={(t) => { return {
                color: t.palette.success.light,
                ml: 'auto',
            }}} />}
        </Box>
        <Grid sx={{ p: 1, }} container spacing={3}>
            <Grid item xs={12} md={4}>
                <FormLabel sx={{ mb: 1 }}>
                    Odchylenie ma wpływ na jakość produktu:&nbsp;
                </FormLabel>
                <If condition={!ro} otherwise={<YesNoChip value={hasImpactOnProductQuality} />}>
                    <RadioGroup row name="wplywNaJakosc" value={hasImpactOnProductQuality ? "YES" : "NO"} onChange={e => setHasImpactOnProductQuality(e.target.value === "YES" ? true : false)}>
                        <FormControlLabel value={"YES"} control={<Radio disabled={ro} />} label="TAK" />
                        <FormControlLabel value={"NO"} control={<Radio disabled={ro} />} label="NIE" />
                    </RadioGroup>
                </If>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormLabel sx={{ mb: 1 }}>Klasyfikacja odchylenia: </FormLabel>
                <If condition={!ro} otherwise={<ClassificationChip value={classification} />}>
                    <RadioGroup name="klasyfikacja" value={classification} onChange={e => setClassification(e.target.value as DeviationClassification)}>
                        <FormControlLabel value="MINOR" control={<Radio disabled={ro} />} label="Drobne" />
                        <FormControlLabel value="MAJOR" control={<Radio disabled={ro} />} label="Ważne" />
                        <FormControlLabel value="CRITICAL" control={<Radio disabled={ro} />} label="Krytyczne" />
                    </RadioGroup>
                </If>
            </Grid>

            <Grid item xs={12} md={4}>
                <FormLabel sx={{ mb: 1, }}>
                    Zezwolenie na kontynuowanie procesu kwalifikacji:&nbsp;
                </FormLabel>
                <If condition={!ro} otherwise={<YesNoChip value={isQualificationProcessContinuationAllowed} />}>
                    <RadioGroup row name="zezwolenieKontynuacja" value={isQualificationProcessContinuationAllowed ? "YES" : "NO"} onChange={e => setIsQualificationProcessContinuationAllowed(e.target.value === "YES" ? true : false)}>
                        <FormControlLabel value="YES" control={<Radio disabled={ro} />} label="TAK" />
                        <FormControlLabel value="NO" control={<Radio disabled={ro} />} label="NIE" />
                    </RadioGroup>
                </If>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    inputProps={{
                        'readonly': ro ? 'true' : undefined
                    }}
                    fullWidth
                    label="Uzasadnienie"
                    multiline
                    minRows={3}
                    value={evaluationJustification}
                    onChange={e => setEvaluationJustification(e.target.value)}
                    error={showErr && !evaluationJustification}
                    helperText={showErr && !evaluationJustification && <RequiredError />}
                />
            </Grid>
        </Grid>

        <Grid container spacing={2}>
            <Grid item md={6}>
                <PersonalDataPicker styles={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    px: 1,
                    mt: 2,
                }}
                    label={'Osoba uprawniona do podjęcia decyzji'}
                    data={decisionMaker}
                    setData={setDecisionMaker}
                    readonly={ro}
                />
            </Grid>
            <Grid item md={6}>
                <DatePicker
                    sx={{ float: 'right', mr: 1 }}
                    label="Data"
                    readOnly={ro}
                    value={decisionMakerSignatureDateValue} onChange={handleDecisionMakerSignatureDateChange} />
            </Grid>
        </Grid>
        {!ro && <ButtonGroup sx={{ mt: 3, px: 1 }}>
            {entity.status === 'ANALYZED' && <LoadingButton loading={saving} variant="contained" onClick={handleEvaluateClick}>Oceń</LoadingButton>}
            {entity.status === 'EVALUATED' && <LoadingButton loading={saving} variant="outlined" onClick={handleSaveClick}>Zapisz</LoadingButton>}
        </ButtonGroup>}
    </Paper>
}

const DeviationAnalysis = (props: { id: number }) => {
    const dispatch = useAppDispatch()

    const entity = useAppSelector(state => docDeviationsSelectors.selectById(state, props.id)) ?? impossible()
    const userId = useAppSelector(selectLoggedUserId)
    const user = useAppSelector(st => selectUserById(st, userId))

    const [saving, setSaving] = useState(false)
    const [showErr, setShowErr] = useState(false)

    const ro = !(entity.status === 'ANALYZED' || entity.status === 'NEW')

    const [rootCause, setRootCause] = useState<string>(entity?.rootCause ?? '')
    const [proposedCorrectiveActions, setProposedCorrectiveActions ] = useState<string>(entity?.proposedCorrectiveActions ?? '')
    const [correctiveActionsDeadline, setCorrectiveActionsDeadline ] = useState<string | null>(entity?.correctiveActionsDeadline ?? null)

    const [analyst, setAnalyst] = useState<PersonData>({
        fullName: entity.analystFullName ?? '',
        userName: entity.analystUserName ?? '',
        email: '',
        position: entity.analystPosition ?? '',
        companyName: entity.analystCompany ?? ''
    })
    const [analystSignatureDate, setAnalystSignatureDate ] = useState< string | null>(entity?.analystSignatureDate ?? '')


    const analystSignatureDateValue: Date | null = analystSignatureDate ? parseISODate(analystSignatureDate) : null
    const handleAnalystSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setAnalystSignatureDate(null)
        } else {
            setAnalystSignatureDate(date.toISOString())
        }
    }
    const correctiveActionsDeadlineValue: Date | null = correctiveActionsDeadline ? parseISODate(correctiveActionsDeadline) : null
    const handleCorrectiveActionsDeadlineSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setCorrectiveActionsDeadline(null)
        } else {
            setCorrectiveActionsDeadline(date.toISOString())
        }
    }

    const handleSaveClick = async () => {
        setShowErr(true)
        if (rootCause && proposedCorrectiveActions && correctiveActionsDeadline !== null) {
            try {
                setSaving(true)
                const saved = await dispatch(analyseDeviation({
                    rootCause,
                    proposedCorrectiveActions,
                    correctiveActionsDeadline,
                    analystUserName: entity.analystUserName,
                    analystFullName: entity.analystFullName,
                    analystPosition: entity.analystPosition,
                    analystCompany: entity.analystCompany,
                    analystSignatureDate,
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setAnalyst({
                    userName: saved.analystUserName,
                    fullName: saved.analystFullName ?? '',
                    email: '',
                    position: saved.analystPosition ?? '',
                    companyName: saved.analystCompany ?? '',
                })
                setAnalystSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    const handleAnalyseClick = async () => {
        setShowErr(true)
        if (rootCause && proposedCorrectiveActions && correctiveActionsDeadline !== null) {
            try {
                setSaving(true)
                const saved = await dispatch(analyseDeviation({
                    rootCause,
                    proposedCorrectiveActions,
                    correctiveActionsDeadline,
                    analystUserName: user?.userName ?? null,
                    analystFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                    analystPosition: user?.position ?? null,
                    analystCompany: user?.companyName ?? null,
                    analystSignatureDate: new Date().toISOString(),
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setAnalyst({
                    userName: saved.analystUserName,
                    fullName: saved.analystFullName ?? '',
                    email: '',
                    position: saved.analystPosition ?? '',
                    companyName: saved.analystCompany ?? '',
                })
                setAnalystSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    return <Paper sx={{ pb: 1, }}>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}
            sx={{
                display: 'flex',
            }}
        >
            <Typography variant="body1" fontWeight={700}>Analiza odchylenia</Typography>
            {ro && <TaskAltIcon sx={(t) => { return {
                color: t.palette.success.light,
                ml: 'auto',
            }}} />}
        </Box>
        <Grid sx={{ p: 1, }} container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    inputProps={{
                        'readonly': ro ? 'true' : undefined
                    }}
                    fullWidth
                    label="Przyczyna źródłowa"
                    multiline
                    minRows={3}
                    value={rootCause}
                    onChange={e => setRootCause(e.target.value)}
                    error={showErr && !rootCause}
                    helperText={showErr && !rootCause && <RequiredError />}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField 
                    inputProps={{
                        'readonly': ro ? 'true' : undefined
                    }}
                    fullWidth
                    label="Proponowane działania naprawcze"
                    multiline
                    minRows={3}
                    value={proposedCorrectiveActions}
                    onChange={e => setProposedCorrectiveActions(e.target.value)}
                    error={showErr && !proposedCorrectiveActions}
                    helperText={showErr && !proposedCorrectiveActions && <RequiredError />}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <DatePicker
                    label="Do kiedy działania powinny zostać wdrożone"
                    readOnly={ro}
                    value={correctiveActionsDeadlineValue}
                    onChange={handleCorrectiveActionsDeadlineSignatureDateChange}
                    slotProps={{
                            textField: {
                                required: showErr && correctiveActionsDeadlineValue === null,
                                helperText: showErr && correctiveActionsDeadlineValue === null && <RequiredError />,
                            },
                        }}
                />
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            <Grid item md={6}>
                <PersonalDataPicker styles={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    px: 1,
                    mt: 2,
                }}
                    label={'Osoba uprawniona do przeprowadzenia analizy'}
                    data={analyst}
                    setData={setAnalyst}
                    readonly={ro}
                />
            </Grid>
            <Grid item md={6}>
                <DatePicker
                    sx={{ float: 'right', mr: 1 }}
                    label="Data"
                    readOnly={ro}
                    value={analystSignatureDateValue} onChange={handleAnalystSignatureDateChange} />
            </Grid>
        </Grid>
        {!ro && <ButtonGroup sx={{ mt: 3, px: 1 }}>
            {entity.status === 'NEW' && <LoadingButton loading={saving} variant="contained" onClick={handleAnalyseClick}>Zapisz analizę</LoadingButton>}
            {entity.status === 'ANALYZED' && <LoadingButton loading={saving} variant="outlined" onClick={handleSaveClick}>Zapisz</LoadingButton>}
        </ButtonGroup>}
    </Paper>
}

const DeviationCorrection = (props: { id: number }) => {
    const dispatch = useAppDispatch()

    const entity = useAppSelector(state => docDeviationsSelectors.selectById(state, props.id)) ?? impossible()
    const userId = useAppSelector(selectLoggedUserId)
    const user = useAppSelector(st => selectUserById(st, userId))

    const [saving, setSaving] = useState(false)
    const [showErr, setShowErr] = useState(false)

    const ro = !(entity.status === 'CORRECTED' || entity.status === 'EVALUATED')

    const hiddenStatuses: DeviationStatus[] = ['NEW', 'ANALYZED']
    const hidden = hiddenStatuses.findIndex(s => s === entity.status) >= 0

    const [performedCorrectiveActions, setPerformedCorrectiveActions ] = useState<string>(entity?.performedCorrectiveActions ?? '')
    const [acceptanceCriteriaMet, setAcceptanceCriteriaMet ] = useState<boolean>(entity?.acceptanceCriteriaMet ?? false)
    const [correctiveActionsRemarks, setCorrectiveActionsRemarks ] = useState< string | null>(entity?.correctiveActionsRemarks ?? '')
    const [retest, setRetest] = useState(entity?.retestDescription === 'YES' ? true : false)

    const [corrector, setCorrector] = useState<PersonData>({
        fullName: entity.correctorFullName ?? '',
        userName: entity.correctorUserName ?? '',
        email: '',
        position: entity.correctorPosition ?? '',
        companyName: entity.correctorCompany ?? ''
    })
    const [correctorSignatureDate, setCorrectorSignatureDate ] = useState< string | null>(entity?.correctorSignatureDate ?? '')
    const correctorSignatureDateValue: Date | null = correctorSignatureDate ? parseISODate(correctorSignatureDate) : null
    const handleCorrectorSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setCorrectorSignatureDate(null)
        } else {
            setCorrectorSignatureDate(date.toISOString())
        }
    }

    const handleSaveClick = async () => {
        setShowErr(true)
        if (performedCorrectiveActions) {
            try {
                setSaving(true)
                const saved = await dispatch(correctionDeviation({
                    performedCorrectiveActions,
                    retestDescription: retest ? 'YES' : 'NO',
                    testProtocolReference: '',
                    acceptanceCriteriaMet,
                    correctiveActionsRemarks: correctiveActionsRemarks ?? '',
                    correctorUserName: entity.correctorUserName,
                    correctorFullName: entity.correctorFullName,
                    correctorPosition: entity.correctorPosition,
                    correctorCompany: entity.correctorCompany,
                    correctorSignatureDate,
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setCorrector({
                    userName: saved.correctorUserName,
                    fullName: saved.correctorFullName ?? '',
                    email: '',
                    position: saved.correctorPosition ?? '',
                    companyName: saved.correctorCompany ?? '',
                })
                setCorrectorSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCorrectionClick = async () => {
        setShowErr(true)
        if (performedCorrectiveActions) {
            try {
                setSaving(true)
                const saved = await dispatch(correctionDeviation({
                    performedCorrectiveActions,
                    retestDescription: retest ? 'YES' : 'NO',
                    testProtocolReference: '',
                    acceptanceCriteriaMet,
                    correctiveActionsRemarks: correctiveActionsRemarks ?? '',
                    correctorUserName: user?.userName ?? null,
                    correctorFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                    correctorPosition: user?.position ?? null,
                    correctorCompany: user?.companyName ?? null,
                    correctorSignatureDate: new Date().toISOString(),
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setCorrector({
                    userName: saved.correctorUserName,
                    fullName: saved.correctorFullName ?? '',
                    email: '',
                    position: saved.correctorPosition ?? '',
                    companyName: saved.correctorCompany ?? '',
                })
                setCorrectorSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    if (hidden) return null
    else return <Paper sx={{ pb: 1, }}>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}
            sx={{
                display: 'flex',
            }}
        >
            <Typography variant="body1" fontWeight={700}>Działania naprawcze/korygujące</Typography>
            {ro && <TaskAltIcon sx={(t) => { return {
                color: t.palette.success.light,
                ml: 'auto',
            }}} />}
        </Box>
        <Grid sx={{ p: 1, }} container spacing={2}>
            <Grid item xs={12}>
                <TextField
                    inputProps={{ 'readonly': ro ? 'true' : undefined }}
                    fullWidth
                    label="Przeprowadzone działania naprawcze/korygujące"
                    multiline
                    minRows={3}
                    value={performedCorrectiveActions}
                    onChange={e => setPerformedCorrectiveActions(e.target.value)}
                    error={showErr && !performedCorrectiveActions}
                    helperText={showErr && !performedCorrectiveActions && <RequiredError />}
                />
            </Grid>
            <Grid item xs={12} md={12}>
                <FormLabel>Wymagane powtórzenie testu: </FormLabel>
                <If condition={!ro} otherwise={<YesNoChip value={retest} />}>
                    <RadioGroup row value={retest ? "YES" : "NO"} onChange={e => setRetest(e.target.value === "YES" ? true : false)}>
                        <FormControlLabel value="YES" control={<Radio disabled={ro} />} label="TAK" />
                        <FormControlLabel value="NO" control={<Radio disabled={ro} />} label="NIE" />
                    </RadioGroup>
                </If>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormLabel sx={{ mb: 1 }}>Spełniono kryteria akceptacji: </FormLabel>
                <If condition={!ro} otherwise={<YesNoChip value={acceptanceCriteriaMet} />}>
                    <RadioGroup row name="spelnionoKryteria" value={acceptanceCriteriaMet ? "YES" : "NO"} onChange={e => setAcceptanceCriteriaMet(e.target.value === "YES" ? true : false)}>
                        <FormControlLabel value="YES" control={<Radio disabled={ro} />} label="TAK" />
                        <FormControlLabel value="NO" control={<Radio disabled={ro} />} label="NIE" />
                    </RadioGroup>
                </If>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    inputProps={{ 'readonly': ro ? 'true' : undefined }}
                    fullWidth label="Uwagi" multiline minRows={3} value={correctiveActionsRemarks} onChange={e => setCorrectiveActionsRemarks(e.target.value)} />
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            <Grid item md={6}>
                <PersonalDataPicker styles={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    px: 1,
                    mt: 2,
                }}
                    label={'Osoba uprawniona do przeprowadzenia działań'}
                    data={corrector}
                    setData={setCorrector}
                    readonly={ro}
                />
            </Grid>
            <Grid item md={6}>
                <DatePicker
                    sx={{ float: 'right', mr: 1 }}
                    label="Data"
                    readOnly={ro}
                    value={correctorSignatureDateValue} onChange={handleCorrectorSignatureDateChange} />
            </Grid>
        </Grid>
        {!ro && <ButtonGroup sx={{ mt: 3, px: 1 }}>
            {entity.status === 'EVALUATED' && <LoadingButton loading={saving} variant="contained" onClick={handleCorrectionClick}>Zapisz dziłania naprawcze/korygujące</LoadingButton>}
            {entity.status === 'CORRECTED' && <LoadingButton loading={saving} variant="outlined" onClick={handleSaveClick}>Zapisz</LoadingButton>}
        </ButtonGroup>}
    </Paper>
}

const DeviationClosure = (props: { id: number }) => {
    const dispatch = useAppDispatch()

    const entity = useAppSelector(state => docDeviationsSelectors.selectById(state, props.id)) ?? impossible()
    const userId = useAppSelector(selectLoggedUserId)
    const user = useAppSelector(st => selectUserById(st, userId))

    const [isDeviationCorrected, setIsDeviationCorrected ] = useState<boolean>(entity?.isDeviationCorrected ?? false)
    const [canDeviationBeClosed, setCanDeviationBeClosed ] = useState<boolean>(entity?.canDeviationBeClosed ?? false)
    const [closingRemarks, setClosingRemarks ] = useState< string | null>(entity?.closingRemarks ?? '')

    const [closer, setCloser] = useState<PersonData>({
        fullName: entity.closerFullName ?? '',
        userName: entity.closerUserName ?? '',
        email: '',
        position: entity.closerPosition ?? '',
        companyName: entity.closerCompany ?? ''
    })
    const [closerSignatureDate, setCloserSignatureDate ] = useState< string | null>(entity?.closerSignatureDate ?? '')

    const [saving, setSaving] = useState(false)

    const closerSignatureDateValue: Date | null = closerSignatureDate ? parseISODate(closerSignatureDate) : null
    const handleCloserSignatureDateChange = (date: Date | null, c: PickerChangeHandlerContext<DateValidationError>) => {
        if (date === null || c.validationError !== null) {
            setCloserSignatureDate(null)
        } else {
            setCloserSignatureDate(date.toISOString())
        }
    }

    const handleSaveClick = async () => {
        if (canDeviationBeClosed === true) {
            try {
                setSaving(true)
                const saved = await dispatch(closeDeviation({
                    isDeviationCorrected,
                    canDeviationBeClosed,
                    closingRemarks: closingRemarks ?? '',
                    closerUserName: user?.userName ?? null,
                    closerFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                    closerPosition: user?.position ?? null,
                    closerCompany: user?.companyName ?? null,
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setCloser({
                    userName: saved.closerUserName,
                    fullName: saved.closerFullName ?? '',
                    email: '',
                    position: saved.closerPosition ?? '',
                    companyName: saved.closerCompany ?? '',
                })
                setCloserSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    const handleCloseClick = async () => {
        if (canDeviationBeClosed === true) {
            try {
                setSaving(true)
                const saved = await dispatch(closeDeviation({
                    isDeviationCorrected,
                    canDeviationBeClosed,
                    closingRemarks: closingRemarks ?? '',
                    closerUserName: user?.userName ?? null,
                    closerFullName: `${user?.firstname ?? '?'} ${user?.surname ?? '?'}`,
                    closerPosition: user?.position ?? null,
                    closerCompany: user?.companyName ?? null,
                    documentId: entity.documentId,
                    id: props.id,
                })).unwrap()
                setCloser({
                    userName: saved.closerUserName,
                    fullName: saved.closerFullName ?? '',
                    email: '',
                    position: saved.closerPosition ?? '',
                    companyName: saved.closerCompany ?? '',
                })
                setCloserSignatureDate(saved.analystSignatureDate)
                dispatch(showSuccess('saved'))
            } catch (error) {
                console.log(error)
                dispatch(showError('error'))
            } finally {
                setSaving(false)
            }
        }
    }

    const hiddenStatuses: DeviationStatus[] = ['NEW', 'EVALUATED', 'ANALYZED']
    const hidden = hiddenStatuses.findIndex(s => s === entity.status) >= 0

    if (hidden) return null
    else return <Paper sx={{ pb: 1, }}>
        <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
            <Typography variant="body1" fontWeight={700}>Zamknięcie odchylenia</Typography>
        </Box>
        <Grid sx={{ p: 1,}} container spacing={2}>
            <Grid item xs={12} md={6}>
                <FormLabel sx={{ mb: 1 }}>Czy odchylenie zostało naprawione?</FormLabel>
                <RadioGroup row name="naprawione" value={isDeviationCorrected ? "YES" : "NO"} onChange={e => setIsDeviationCorrected(e.target.value === "YES" ? true : false)}>
                    <FormControlLabel value="YES" control={<Radio />} label="TAK" />
                    <FormControlLabel value="NO" control={<Radio />} label="NIE" />
                </RadioGroup>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormLabel sx={{ mb: 1 }}>Czy odchylenie można zamknąć?</FormLabel>
                <RadioGroup row name="moznaZamknac" value={canDeviationBeClosed ? "YES" : "NO"} onChange={e => setCanDeviationBeClosed(e.target.value === "YES" ? true : false)}>
                    <FormControlLabel value="YES" control={<Radio />} label="TAK" />
                    <FormControlLabel value="NO" control={<Radio />} label="NIE" />
                </RadioGroup>
            </Grid>
            <Grid item xs={12} md={6}>
                <TextField fullWidth label="Uwagi" multiline minRows={3} value={closingRemarks} onChange={e => setClosingRemarks(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
                <RetestResultTable deviation={entity} />
            </Grid>
        </Grid>
        <Grid container spacing={2}>
            <Grid item md={6}>
                <PersonalDataPicker styles={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    px: 1,
                    mt: 2,
                }}
                    label={'Osoba uprawnione do zamknięcia odchylenia'}
                    data={closer}
                    setData={setCloser}
                />
            </Grid>
            <Grid item md={6}>
                <DatePicker
                    sx={{ float: 'right', mr: 1 }}
                    label="Data"
                    value={closerSignatureDateValue} onChange={handleCloserSignatureDateChange} />
            </Grid>
        </Grid>
        <ButtonGroup sx={{ mt: 3, px: 1 }}>
            {entity.status === 'CORRECTED' && <LoadingButton loading={saving} variant="contained" onClick={handleCloseClick}>Zamknij odchylenie</LoadingButton>}
            {entity.status === 'CLOSED' && <LoadingButton loading={saving} variant="outlined" onClick={handleSaveClick}>Zapisz</LoadingButton>}
        </ButtonGroup>
    </Paper>
}

const RetestResultTable = (props: { deviation: Deviation }) => {
    const { tests } = props.deviation

    if (props.deviation.retestDescription !== 'YES') return null

    return <TableContainer component={Paper}>
        <Table size='small'>
            <caption><small>Wyniki powtórzonego testu</small></caption>
            <TableHead>
                <TableCell width={60}>#</TableCell>
                <TableCell>Nazwa testu</TableCell>
                <TableCell width={160} align='center'>Powtórzony</TableCell>
                <TableCell width={160}>Data testu</TableCell>
                <TableCell width={160}>Wynik</TableCell>
            </TableHead>
            <TableBody>
                {tests.map((t, idx) => <TableRow>
                    <TableCell>{idx + 1}</TableCell>
                    <RetestInfoRow deviation={props.deviation} id={t.documentTestId} />
                </TableRow>)}
            </TableBody>
        </Table>
    </TableContainer>
}

const RetestInfoRow = (props: { deviation: Deviation, id: number }) => {
    const { deviation, id } = props

    const loaded = useAppSelector(state => selectDocument(state).doc?.tests.loaded ?? false)
    const test = useAppSelector(state => docTestsSelectors.selectById(state, id))

    const lastRun = test && test.lastRunExecutedAt ? parseISODate(test.lastRunExecutedAt) : null
    const devDate = deviation.correctorSignatureDate ? parseISODate(deviation.correctorSignatureDate) : null

    const retested = lastRun && devDate && lastRun > devDate ? true : false

    const lastRunFormatted = lastRun ? formatDate(lastRun) : ''
    const devFormatted = devDate ? formatDate(devDate) : ''

    if (!loaded) return <>
        <TableCell>
            <Typography fontSize='small' color='text-secondary'><Localized id='loading-data'>Ładowanie danych...</Localized></Typography>
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
    </>

    return <>
        <TableCell>{test?.testSpecification.name}</TableCell>
        <TableCell align='center'>
            {retested ? <CheckIcon color='success' /> : <CloseIcon color='error' />}
        </TableCell>
        <TableCell>{lastRunFormatted}</TableCell>
        <TableCell>
            <DocumentTestOutcomeChip outcome={test?.lastRunOutcome} />
        </TableCell>
    </>
}
